/**
 * simDownloadQueueStore
 *
 * A framework-agnostic singleton that tracks sim template file downloads
 * running in the background. Uses the server's /StreamDownloadBlob endpoint
 * which streams blob content directly from Azure without buffering in memory.
 * Progress is tracked via ReadableStream on the fetch response.
 *
 * Flow:
 *  1. Caller registers a download with upload type and blob path.
 *  2. This store calls /StreamDownloadBlob (server streams from Azure).
 *  3. The fetch response is read via ReadableStream for progress tracking.
 *  4. On completion it triggers a browser download via an object URL.
 *  5. React components subscribe() to receive live status updates
 *     for the banner.
 */

import { AccessTokenKey, GMMSRaceTeamKey } from '../utils/constants'

export type SimDownloadStatus = 'downloading' | 'done' | 'failed' | 'cancelled'

export type PendingSimDownload = {
  id: string
  fileName: string
  methodologyName: string
  status: SimDownloadStatus
  /** Download percentage 0-100 */
  progress?: number
  error?: string
  /** AbortController to allow cancellation */
  abortController?: AbortController
}

type Listener = (downloads: PendingSimDownload[]) => void

let pendingDownloads: PendingSimDownload[] = []
const listeners: Set<Listener> = new Set()

const notify = (): void => {
  const snapshot = pendingDownloads.map(({ abortController, ...rest }) => rest)
  listeners.forEach(l => {
    try {
      l(snapshot)
    } catch (_) {
      // swallow listener errors
    }
  })
}

/**
 * Simple throttle helper to limit how often notify() is called for progress updates.
 */
const createThrottle = (fn: () => void, wait: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let lastCallTime = 0
  let pendingCall = false

  return () => {
    const now = Date.now()
    const timeSinceLastCall = now - lastCallTime

    if (timeSinceLastCall >= wait) {
      lastCallTime = now
      fn()
      pendingCall = false
    } else {
      pendingCall = true
      if (!timeoutId) {
        timeoutId = setTimeout(() => {
          timeoutId = null
          if (pendingCall) {
            lastCallTime = Date.now()
            fn()
            pendingCall = false
          }
        }, wait - timeSinceLastCall)
      }
    }
  }
}

const throttledNotify = createThrottle(notify, 100)

const simDownloadQueueStore = {
  /**
   * Register and start a sim template download via the server streaming proxy.
   *
   * @param params      Metadata about the file being downloaded.
   * @param downloadParams  Parameters for the /StreamDownloadBlob endpoint.
   */
  register(
    params: {
      id: string
      fileName: string
      methodologyName: string
    },
    downloadParams: {
      uploadType: string
      blobPath: string
      gmid?: string
    }
  ): void {
    // Avoid duplicate registrations
    if (pendingDownloads.find(d => d.id === params.id)) {
      return
    }

    const abortController = new AbortController()

    const entry: PendingSimDownload = {
      id: params.id,
      fileName: params.fileName,
      methodologyName: params.methodologyName,
      status: 'downloading',
      progress: 0,
      abortController
    }
    pendingDownloads = [...pendingDownloads, entry]
    notify()

    // Start the download
    this._performDownload(params.id, params.fileName, downloadParams, abortController)
  },

  async _performDownload(
    id: string,
    fileName: string,
    downloadParams: { uploadType: string; blobPath: string; gmid?: string },
    abortController: AbortController
  ): Promise<void> {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || ''
      const accessToken = localStorage.getItem(AccessTokenKey)
      const teamId = localStorage.getItem(GMMSRaceTeamKey)

      const response = await fetch(`${baseUrl}/StreamDownloadBlob`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          GMMSRaceTeamId: teamId || ''
        },
        body: JSON.stringify({
          uploadType: downloadParams.uploadType,
          blobPath: downloadParams.blobPath,
          ...(downloadParams.gmid && { gmid: downloadParams.gmid })
        }),
        signal: abortController.signal
      })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`)
      }

      const contentLength = response.headers.get('content-length')
      const totalBytes = contentLength ? parseInt(contentLength, 10) : 0

      if (!response.body) {
        // Fallback: no streaming support, download as blob directly
        const blob = await response.blob()
        this._triggerBrowserDownload(blob, fileName)
        pendingDownloads = pendingDownloads.map(d =>
          d.id === id ? { ...d, status: 'done' as const, progress: 100 } : d
        )
        notify()
        return
      }

      const reader = response.body.getReader()
      const chunks: Uint8Array[] = []
      let receivedBytes = 0

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        chunks.push(value)
        receivedBytes += value.length

        if (totalBytes > 0) {
          const progress = Math.round((receivedBytes / totalBytes) * 100)
          pendingDownloads = pendingDownloads.map(d => (d.id === id ? { ...d, progress } : d))
          throttledNotify()
        }
      }

      // Assemble downloaded chunks into a Blob and trigger browser download
      const blob = new Blob(chunks as BlobPart[])
      this._triggerBrowserDownload(blob, fileName)

      pendingDownloads = pendingDownloads.map(d =>
        d.id === id ? { ...d, status: 'done' as const, progress: 100 } : d
      )
      notify()
    } catch (err: unknown) {
      if (abortController.signal.aborted) {
        pendingDownloads = pendingDownloads.map(d =>
          d.id === id ? { ...d, status: 'cancelled' as const } : d
        )
      } else {
        const message = err instanceof Error ? err.message : 'Unexpected error'
        pendingDownloads = pendingDownloads.map(d =>
          d.id === id ? { ...d, status: 'failed' as const, error: message } : d
        )
      }
      notify()
    }
  },

  _triggerBrowserDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  /** Get a point-in-time snapshot (without abortControllers). */
  getAll(): Omit<PendingSimDownload, 'abortController'>[] {
    return pendingDownloads.map(({ abortController, ...rest }) => rest)
  },

  /** Cancel an active download. */
  cancel(id: string): void {
    const download = pendingDownloads.find(d => d.id === id)
    if (download?.abortController) {
      download.abortController.abort()
    }
  },

  /** Dismiss a completed/failed/cancelled entry from the list. */
  dismiss(id: string): void {
    pendingDownloads = pendingDownloads.filter(d => d.id !== id)
    notify()
  },

  /** Subscribe to changes. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },

  /** True if any downloads are actively in-progress. */
  hasActiveDownloads(): boolean {
    return pendingDownloads.some(d => d.status === 'downloading')
  }
}

export default simDownloadQueueStore
